import { Document, Page, Text, View, StyleSheet } from "@react-pdf/renderer";

import ReportHeader from "../cash-register/ReportHeader";
import ReportFooter from "../cash-register/ReportFooter";
import { Colors } from "../../../configs";
import { pxToRem } from "../../../util";
import { IPieceCountReportRow } from "../../../typings/interfaces/responses/customer-report-data-response";
import { NotesBottomBlock } from "../../../util/report-notes-utils";

export interface DeptWisePieceCountCustomReportProps {
  rows: IPieceCountReportRow[];
  customerName: string;
  customerRef: string;
  address: string;
  contactNumber: string;
  reportTitle: string;
  period: string;
  notes?: string;
}

const styles = StyleSheet.create({
  page: { fontSize: 12, fontFamily: "Helvetica" },
  tableContainer: { marginTop: 10, borderWidth: 1, borderColor: "#A1CEF5", borderRadius: 3 },
  tableHeader: { backgroundColor: "#A1CEF5", flexDirection: "row", padding: 4 },
  tableRow: { flexDirection: "row", padding: 4 },
  cell: { fontSize: 6.5, textAlign: "center", padding: 3 },
  colDate: { width: "8%" },
  colDept: { width: "8.5%" },
  colTotal: { width: "9%" },
  infoRow: { flexDirection: "row", marginBottom: 4 },
  infoLabel: { width: "35%", fontSize: 8 },
  infoColon: { width: "5%", fontSize: 8, textAlign: "center" },
  infoValue: { width: "60%", fontSize: 8, paddingLeft: 8 },
});

const chunkArray = <T,>(arr: T[], size: number): T[][] =>
  Array.from({ length: Math.ceil(arr.length / size) }, (_, i) => arr.slice(i * size, i * size + size));

const DeptWisePieceCountCustomReport = ({
  rows,
  customerName,
  customerRef,
  address,
  contactNumber,
  reportTitle,
  period,
  notes,
}: DeptWisePieceCountCustomReportProps) => {
  const totals = rows.reduce(
    (acc, r) => ({
      laundry: acc.laundry + r.laundry,
      dryCleaning: acc.dryCleaning + r.dryCleaning,
      household: acc.household + r.household,
      alteration: acc.alteration + r.alteration,
      furLeather: acc.furLeather + r.furLeather,
      shopRepair: acc.shopRepair + r.shopRepair,
      costumes: acc.costumes + r.costumes,
      washFold: acc.washFold + r.washFold,
      miscellaneous: acc.miscellaneous + r.miscellaneous,
      retails: acc.retails + r.retails,
      total: acc.total + r.total,
    }),
    { laundry: 0, dryCleaning: 0, household: 0, alteration: 0, furLeather: 0, shopRepair: 0, costumes: 0, washFold: 0, miscellaneous: 0, retails: 0, total: 0 }
  );

  const chunks = chunkArray(rows.length > 0 ? rows : [{ label: "—", laundry: 0, dryCleaning: 0, household: 0, alteration: 0, furLeather: 0, shopRepair: 0, costumes: 0, washFold: 0, miscellaneous: 0, retails: 0, total: 0 }], 18);

  return (
    <Document>
      {chunks.map((chunk, pageIndex, pages) => (
        <Page size="A4" style={styles.page} orientation="landscape" key={pageIndex}>
          <ReportHeader />
          <View style={{ width: "760px", backgroundColor: Colors.REPORT_WHITE, borderRadius: 15, marginLeft: 48, padding: 18 }}>
            <View style={{ flexDirection: "column", height: pxToRem(510) }}>
              <View style={{ marginBottom: pageIndex === 0 ? 12 : 20 }}>
                <Text style={{ fontSize: 14, fontWeight: "bold", color: "#505050" }}>
                  {pageIndex === 0 ? (reportTitle || "Dept-Wise Piece Count") : `${reportTitle || "Dept-Wise Piece Count"} (Contd.)`}
                </Text>
              </View>

              {pageIndex === 0 && (
                <View style={{ marginBottom: 12, flexDirection: "row", flexWrap: "wrap" }}>
                  {[
                    ["Customer Name", customerName],
                    ["Customer ID", customerRef],
                    ["Address", address],
                    ["Contact Number", contactNumber],
                  ].map(([label, value]) => (
                    <View key={label} style={[styles.infoRow, { width: "50%" }]}>
                      <Text style={styles.infoLabel}>{label}</Text>
                      <Text style={styles.infoColon}>:</Text>
                      <Text style={styles.infoValue}>{value}</Text>
                    </View>
                  ))}
                </View>
              )}

              <View style={styles.tableContainer}>
                <View style={styles.tableHeader}>
                  <Text style={[styles.cell, styles.colDate]}>Date</Text>
                  <Text style={[styles.cell, styles.colDept]}>Laundry</Text>
                  <Text style={[styles.cell, styles.colDept]}>Dry Clean</Text>
                  <Text style={[styles.cell, styles.colDept]}>Household</Text>
                  <Text style={[styles.cell, styles.colDept]}>Alteration</Text>
                  <Text style={[styles.cell, styles.colDept]}>Fur/Leather</Text>
                  <Text style={[styles.cell, styles.colDept]}>Shop Repair</Text>
                  <Text style={[styles.cell, styles.colDept]}>Costumes</Text>
                  <Text style={[styles.cell, styles.colDept]}>Wash & Fold</Text>
                  <Text style={[styles.cell, styles.colDept]}>Misc.</Text>
                  <Text style={[styles.cell, styles.colDept]}>Retails</Text>
                  <Text style={[styles.cell, styles.colTotal]}>Total</Text>
                </View>
                {chunk.map((row, idx) => (
                  <View key={idx} style={styles.tableRow}>
                    <Text style={[styles.cell, styles.colDate]}>{row.label}</Text>
                    <Text style={[styles.cell, styles.colDept]}>{row.laundry}</Text>
                    <Text style={[styles.cell, styles.colDept]}>{row.dryCleaning}</Text>
                    <Text style={[styles.cell, styles.colDept]}>{row.household}</Text>
                    <Text style={[styles.cell, styles.colDept]}>{row.alteration}</Text>
                    <Text style={[styles.cell, styles.colDept]}>{row.furLeather}</Text>
                    <Text style={[styles.cell, styles.colDept]}>{row.shopRepair}</Text>
                    <Text style={[styles.cell, styles.colDept]}>{row.costumes}</Text>
                    <Text style={[styles.cell, styles.colDept]}>{row.washFold}</Text>
                    <Text style={[styles.cell, styles.colDept]}>{row.miscellaneous}</Text>
                    <Text style={[styles.cell, styles.colDept]}>{row.retails}</Text>
                    <Text style={[styles.cell, styles.colTotal]}>{row.total}</Text>
                  </View>
                ))}
                {pageIndex === pages.length - 1 && (
                  <View style={[styles.tableRow, { backgroundColor: "#A1CEF5" }]}>
                    <Text style={[styles.cell, styles.colDate]}>Total</Text>
                    <Text style={[styles.cell, styles.colDept]}>{totals.laundry}</Text>
                    <Text style={[styles.cell, styles.colDept]}>{totals.dryCleaning}</Text>
                    <Text style={[styles.cell, styles.colDept]}>{totals.household}</Text>
                    <Text style={[styles.cell, styles.colDept]}>{totals.alteration}</Text>
                    <Text style={[styles.cell, styles.colDept]}>{totals.furLeather}</Text>
                    <Text style={[styles.cell, styles.colDept]}>{totals.shopRepair}</Text>
                    <Text style={[styles.cell, styles.colDept]}>{totals.costumes}</Text>
                    <Text style={[styles.cell, styles.colDept]}>{totals.washFold}</Text>
                    <Text style={[styles.cell, styles.colDept]}>{totals.miscellaneous}</Text>
                    <Text style={[styles.cell, styles.colDept]}>{totals.retails}</Text>
                    <Text style={[styles.cell, styles.colTotal]}>{totals.total}</Text>
                  </View>
                )}
              </View>
              {pageIndex === pages.length - 1 && <NotesBottomBlock notes={notes} />}
            </View>
          </View>
          <ReportFooter />
        </Page>
      ))}
    </Document>
  );
};

export default DeptWisePieceCountCustomReport;
